import React, { useState, useEffect, useRef } from 'react';
import { getSoldProperties, getPropertyImages } from '../api';

const CARDS_PER_PAGE = 12;

function SoldProperties() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [propertyImages, setPropertyImages] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const gridRef = useRef(null);

  useEffect(() => {
    fetchSoldProperties();
  }, []);

  const fetchSoldProperties = async () => {
    try {
      const response = await getSoldProperties();
      setProperties(response.data);

      // Load first image for each property
      const imagePromises = response.data.map(async (property) => {
        try {
          const imgResponse = await getPropertyImages(property.id);
          return { propertyId: property.id, images: imgResponse.data };
        } catch (err) {
          return { propertyId: property.id, images: [] };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      const imagesMap = {};
      imageResults.forEach(result => {
        imagesMap[result.propertyId] = result.images;
      });
      setPropertyImages(imagesMap);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching sold properties:', error);
      setLoading(false);
    }
  };

  const filteredProperties = properties.filter(property => {
    if (filter === 'all') return true;
    return property.status === filter;
  });

  // Client-side pagination. Same slice arithmetic as PaymentTracking.js, the
  // app's existing pager. The endpoint hands back every row in one response,
  // so there is nothing to fetch on a page change.
  //
  // This block sits above the `loading` early return on purpose: the useEffect
  // below has to run on every render, or the hook order changes between the
  // loading and loaded states.
  const totalPages = Math.ceil(filteredProperties.length / CARDS_PER_PAGE);

  // Clamped rather than stored. If the row count shrinks under the current
  // page, deriving the page keeps the grid from rendering empty, and costs no
  // extra render the way a corrective setState would.
  const safePage = Math.min(currentPage, Math.max(1, totalPages));
  const startIndex = (safePage - 1) * CARDS_PER_PAGE;
  const paginatedProperties = filteredProperties.slice(startIndex, startIndex + CARDS_PER_PAGE);

  // Switching tabs starts the new filtered set at page 1.
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Scrolling lives in the click handler, not in an effect on currentPage:
  // an effect would also fire on mount and on the tab reset above, yanking the
  // page down when the user had not asked to move.
  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(1, page), Math.max(1, totalPages)));
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Window of up to 7 numbers, matching PaymentTracking.js.
  const pageNumbers = Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
    if (totalPages <= 7) return i + 1;
    if (safePage <= 4) return i + 1;
    if (safePage >= totalPages - 3) return totalPages - 6 + i;
    return safePage - 3 + i;
  });

  if (loading) {
    return <div className="sold-properties-page"><p>Loading...</p></div>;
  }

  return (
    <div className="sold-properties-page">
      <h1>Recent Sales & Active Contracts</h1>
      <p className="subtitle">See what our customers are buying! These properties have been sold or are currently under contract.</p>
      <p className="sales-milestone">Over 100 parcels sold since 2020, most financed on terms.</p>

      {/* Filter Tabs */}
      <div className="filter-tabs">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All ({properties.length})
        </button>
        <button 
          className={filter === 'pending' ? 'active' : ''}
          onClick={() => setFilter('pending')}
        >
          Pending ({properties.filter(p => p.status === 'pending').length})
        </button>
        <button 
          className={filter === 'under_contract' ? 'active' : ''}
          onClick={() => setFilter('under_contract')}
        >
          Under Contract ({properties.filter(p => p.status === 'under_contract').length})
        </button>
        <button 
          className={filter === 'sold' ? 'active' : ''}
          onClick={() => setFilter('sold')}
        >
          Sold ({properties.filter(p => p.status === 'sold').length})
        </button>
      </div>

      {/* Properties Grid */}
      {filteredProperties.length === 0 ? (
        <p className="no-properties">No properties found in this category.</p>
      ) : (
        <div className="properties-grid" ref={gridRef}>
          {paginatedProperties.map(property => (
            <div key={property.id} className="property-card sold-property-card">
              {/* Status Badge */}
              <div className={`status-badge ${property.status === 'sold' ? 'badge-sold' : property.status === 'under_contract' ? 'badge-contract' : 'badge-pending'}`}>
                {property.status === 'sold' ? '✓ SOLD' : property.status === 'under_contract' ? '📝 UNDER CONTRACT' : '⏳ PENDING'}
              </div>

              {/* Property Image */}
              {propertyImages[property.id]?.length > 0 ? (
                <img
                  src={propertyImages[property.id][0].url}
                  alt={property.title}
                  className="property-image"
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover'
                  }}
                />
              ) : (
                <img
                  src="https://res.cloudinary.com/dxd4ef2tc/image/upload/w_400/IMAGES-COMING-SOON_tbspdc.png"
                  alt="Property placeholder"
                  className="property-image property-placeholder-image"
                  style={{
                    width: '100%',
                    height: '250px',
                    objectFit: 'cover'
                  }}
                />
              )}

              {/* Property Details */}
              <div className="property-details">
                <h3>{property.title}</h3>
                <p className="property-location">📍 {property.location}</p>
                <div style={{ margin: '0.75rem 0' }}>
                  <span className="property-acres" style={{ fontSize: '1.1rem', color: 'var(--color-brand-text)', fontWeight: '600' }}>
                    🌲 {property.acres} Acres
                  </span>
                </div>
                <p className="sale-date">
                  {property.status === 'sold' ? 'Sold' : property.status === 'under_contract' ? 'Under Contract' : 'Pending'}: {new Date(property.created_at).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hidden entirely when the active filter fits on one page. An empty
          filtered set gives totalPages 0, so this covers that case too. */}
      {totalPages > 1 && (
        <nav className="pagination" aria-label="Sold properties pagination">
          <button
            className="pagination-btn pagination-nav"
            onClick={() => goToPage(safePage - 1)}
            disabled={safePage === 1}
            aria-label="Previous page"
          >
            <span aria-hidden="true">←</span>
            <span className="pagination-label">Previous</span>
          </button>

          {pageNumbers.map(num => (
            <button
              key={num}
              className={'pagination-btn pagination-page' + (num === safePage ? ' active' : '')}
              onClick={() => goToPage(num)}
              aria-label={'Page ' + num}
              aria-current={num === safePage ? 'page' : undefined}
            >
              {num}
            </button>
          ))}

          <button
            className="pagination-btn pagination-nav"
            onClick={() => goToPage(safePage + 1)}
            disabled={safePage === totalPages}
            aria-label="Next page"
          >
            <span className="pagination-label">Next</span>
            <span aria-hidden="true">→</span>
          </button>
        </nav>
      )}
    </div>
  );
}

export default SoldProperties;